import React, {
  FC,
  MouseEvent,
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
} from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import {
  DocBlock,
  DocInline,
  blockText,
  estimateCharBudget,
  findAnchorPage,
  paginateBlocks,
  sectionIndex,
} from './paginate';
import { useElementSize, usePager, useSwipePaging } from './hooks';

export interface PagerControlsProps {
  page: number;
  pageCount: number;
  onPrev: () => void;
  onNext: () => void;
  /** What a page is called in the status text ("Page", "Rule"…). */
  unit?: string;
  /** Accessible name for the controls region. */
  label?: string;
  /** Rendered between the status and the buttons (e.g. a section picker). */
  extra?: ReactNode;
}

/** Previous / next (44px) with a visible "Page n of N" and progress bar. */
export const PagerControls: FC<PagerControlsProps> = ({
  page,
  pageCount,
  onPrev,
  onNext,
  unit = 'Page',
  label = 'Pages',
  extra,
}) => {
  const pct = pageCount > 1 ? Math.round(((page + 1) / pageCount) * 100) : 100;
  return (
    <nav className="bh-pager__controls" aria-label={label}>
      <button
        type="button"
        className="bh-btn bh-btn--secondary bh-btn--small bh-pager__btn"
        onClick={onPrev}
        disabled={page <= 0}
        aria-label={`Previous ${unit.toLowerCase()}`}
      >
        <ChevronLeft size={20} aria-hidden="true" />
        <span className="bh-pager__btn-text">Back</span>
      </button>
      <div className="bh-pager__status">
        <p className="bh-pager__count" aria-live="polite" aria-atomic="true">
          {unit} {page + 1} of {pageCount}
        </p>
        <div
          className="bh-pager__track"
          role="progressbar"
          aria-label="Reading progress"
          aria-valuemin={1}
          aria-valuemax={pageCount}
          aria-valuenow={page + 1}
          aria-valuetext={`${unit} ${page + 1} of ${pageCount}`}
        >
          <span style={{ width: `${pct}%` }} />
        </div>
        {extra}
      </div>
      <button
        type="button"
        className="bh-btn bh-btn--primary bh-btn--small bh-pager__btn"
        onClick={onNext}
        disabled={page >= pageCount - 1}
        aria-label={`Next ${unit.toLowerCase()}`}
      >
        <span className="bh-pager__btn-text">Next</span>
        <ChevronRight size={20} aria-hidden="true" />
      </button>
    </nav>
  );
};

const InlineRun: FC<{
  run: DocInline;
  onAnchor: (id: string) => void;
}> = ({ run, onAnchor }) => {
  const body = run.strong ? <strong>{run.text}</strong> : run.text;
  if (!run.href) return <>{body}</>;
  if (run.href.startsWith('#')) {
    const id = run.href.slice(1);
    return (
      <a
        href={run.href}
        className="bh-link"
        onClick={(e: MouseEvent) => {
          e.preventDefault();
          onAnchor(id);
        }}
      >
        {body}
      </a>
    );
  }
  return (
    <a
      href={run.href}
      className="bh-link"
      target="_blank"
      rel="noopener noreferrer"
    >
      {body}
    </a>
  );
};

const BlockView: FC<{
  block: DocBlock;
  onAnchor: (id: string) => void;
}> = ({ block, onAnchor }) => {
  const runs = block.inlines.map((run, i) => (
    <InlineRun key={i} run={run} onAnchor={onAnchor} />
  ));
  const anchor = block.anchors?.[0] ? `doc-${block.anchors[0]}` : undefined;
  const focusable = anchor ? { tabIndex: -1 } : {};
  switch (block.kind) {
    case 'title':
      return (
        <h2 className="bh-doc__title" id={anchor} {...focusable}>
          {runs}
        </h2>
      );
    case 'h1':
      return (
        <h3 className="bh-doc__h1" id={anchor} {...focusable}>
          {runs}
        </h3>
      );
    case 'h2':
      return (
        <h4 className="bh-doc__h2" id={anchor} {...focusable}>
          {runs}
        </h4>
      );
    case 'subtitle':
      return (
        <p className="bh-doc__subtitle" id={anchor} {...focusable}>
          {runs}
        </p>
      );
    case 'li':
      return (
        <p className="bh-doc__li" id={anchor} {...focusable}>
          {runs}
        </p>
      );
    default:
      return (
        <p className="bh-doc__p" id={anchor} {...focusable}>
          {runs}
        </p>
      );
  }
};

export interface PagedDocumentProps {
  blocks: DocBlock[];
  /** Accessible name of the reading region. */
  label: string;
  /** Status unit, defaults to "Page". */
  unit?: string;
  /** Renders numbered rule cards instead of prose. */
  variant?: 'prose' | 'rules';
  /** Show a "Jump to section" picker built from top-level headings. */
  sectionPicker?: boolean;
  className?: string;
  /** Font metrics used to estimate how much text fits a page. */
  fontPx?: number;
}

/**
 * Long text as bounded pages: the text area is measured and the document is
 * split so each page fits without scrolling; previous / next, swipe and ←/→
 * move between pages. Every block is on exactly one page — nothing is hidden.
 * The page itself keeps `overflow-y: auto` only as a safety net for extreme
 * user font scaling.
 */
export const PagedDocument: FC<PagedDocumentProps> = ({
  blocks,
  label,
  unit = 'Page',
  variant = 'prose',
  sectionPicker = false,
  className,
  fontPx = 16,
}) => {
  const pageRef = useRef<HTMLDivElement>(null);
  const regionRef = useRef<HTMLDivElement>(null);
  const { width, height } = useElementSize(pageRef);

  const budget = estimateCharBudget(width, height, {
    fontPx,
    lineHeight: variant === 'rules' ? 1.5 : 1.55,
  });
  const pages = useMemo(() => paginateBlocks(blocks, budget), [blocks, budget]);
  const pager = usePager(pages.length);
  const sections = useMemo(
    () => (sectionPicker ? sectionIndex(pages) : []),
    [pages, sectionPicker]
  );

  // Keep the reader on the same text when the page size changes (rotation,
  // keyboard, split view): remember the first block of the current page.
  const firstBlock = useRef<DocBlock | null>(null);
  const { goTo } = pager;
  useEffect(() => {
    const anchorBlock = firstBlock.current;
    if (!anchorBlock) return;
    const text = blockText(anchorBlock);
    const idx = pages.findIndex(p => p.some(b => blockText(b) === text));
    if (idx >= 0) goTo(idx);
    // Only when the pagination itself changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pages]);
  const currentPage = pages[pager.page] || [];
  firstBlock.current = currentPage[0] || null;

  useEffect(() => {
    pageRef.current?.scrollTo?.({ top: 0 });
  }, [pager.page]);

  const onAnchor = useCallback(
    (id: string) => {
      const target = findAnchorPage(pages, id);
      if (target < 0) return;
      const block = pages[target].find(b => b.anchors?.includes(id));
      goTo(target);
      window.requestAnimationFrame(() => {
        const el = document.getElementById(`doc-${block?.anchors?.[0]}`);
        el?.focus?.();
      });
    },
    [pages, goTo]
  );

  useSwipePaging(regionRef, pager.prev, pager.next);

  // Rule numbering continues across pages.
  const startNumber =
    pages.slice(0, pager.page).reduce((n, p) => n + p.length, 0) + 1;

  const picker =
    sectionPicker && sections.length > 1 ? (
      <label className="bh-pager__jump">
        <span className="st-sr-only">Jump to section</span>
        <select
          value=""
          onChange={e => {
            const p = Number(e.target.value);
            if (!Number.isNaN(p)) goTo(p);
          }}
        >
          <option value="" disabled>
            Jump to section…
          </option>
          {sections.map(s => (
            <option key={`${s.label}-${s.page}`} value={s.page}>
              {s.label}
            </option>
          ))}
        </select>
      </label>
    ) : null;

  return (
    <div
      ref={regionRef}
      className={`bh-pager bh-pager--${variant} ${className || ''}`}
      role="region"
      aria-label={label}
      aria-roledescription="paged document"
    >
      <div
        ref={pageRef}
        className="bh-pager__page"
        tabIndex={0}
        aria-label={`${unit} ${pager.page + 1} of ${pager.pageCount}`}
      >
        {variant === 'rules' ? (
          <ol
            className="bh-rules"
            start={startNumber}
            style={{ counterReset: `rule ${startNumber - 1}` }}
          >
            {currentPage.map((block, i) => (
              <li key={`${pager.page}-${i}`}>
                <span>
                  {block.inlines.map((run, j) => (
                    <InlineRun key={j} run={run} onAnchor={onAnchor} />
                  ))}
                </span>
              </li>
            ))}
          </ol>
        ) : (
          <div className="bh-doc">
            {currentPage.map((block, i) => (
              <BlockView
                key={`${pager.page}-${i}`}
                block={block}
                onAnchor={onAnchor}
              />
            ))}
          </div>
        )}
      </div>
      {pager.pageCount > 1 && (
        <PagerControls
          page={pager.page}
          pageCount={pager.pageCount}
          onPrev={pager.prev}
          onNext={pager.next}
          unit={unit}
          label={`${label} pages`}
          extra={picker}
        />
      )}
    </div>
  );
};
